import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { BAR_COLOURS, type BarColour, type BarProps } from "@/lib/trainDom"

export interface BarModalState {
  el: Element
  props: BarProps
  columns: string[]
  isNew: boolean
}

/** Modal editor for a matrix ("Streams of work") bar: rich text, the day span
 *  (start/end columns), colour, milestone, show/hide, and delete. */
export function BarModal({
  state,
  onApply,
  onCancel,
  onDelete,
}: {
  state: BarModalState
  onApply: (p: BarProps) => void
  onCancel: () => void
  onDelete: () => void
}) {
  const { props, columns, isNew } = state
  const textRef = useRef<HTMLDivElement>(null)
  const [start, setStart] = useState(props.startIdx)
  const [end, setEnd] = useState(props.endIdx)
  const [colour, setColour] = useState<BarColour>(props.colour)
  const [ms, setMs] = useState(props.ms)
  const [hidden, setHidden] = useState(props.hidden)

  // Prefill the rich-text field (chips/code are preserved as inert nodes).
  useEffect(() => {
    if (textRef.current) textRef.current.innerHTML = props.text
  }, [props.text])

  const apply = () =>
    onApply({
      text: textRef.current?.innerHTML ?? "",
      startIdx: start,
      endIdx: Math.max(start, end),
      colour,
      ms,
      hidden,
    })

  const dayOptions = columns.map((label, i) => (
    <option key={i} value={i}>
      {label || `Col ${i + 1}`}
    </option>
  ))

  // Portal to <body>: the app has a transformed ancestor, which would otherwise
  // trap position:fixed and push the centered modal far off-screen.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md space-y-3 rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">
            {isNew ? "New stream item" : "Edit stream item"}
          </h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            ✕
          </button>
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Text
          <div
            ref={textRef}
            contentEditable
            suppressContentEditableWarning
            className="mt-1 min-h-[38px] rounded-md border border-slate-300 px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-slate-800 focus:outline-none focus:ring-2 focus:ring-header/40"
          />
        </label>

        <div className="flex flex-wrap gap-3">
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Start day
            <select
              value={start}
              onChange={(e) => setStart(Number(e.target.value))}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
            >
              {dayOptions}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            End day
            <select
              value={end}
              onChange={(e) => setEnd(Number(e.target.value))}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
            >
              {dayOptions}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Colour
            <select
              value={colour}
              onChange={(e) => setColour(e.target.value as BarColour)}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
            >
              {BAR_COLOURS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={ms} onChange={(e) => setMs(e.target.checked)} />
            Milestone (single moment)
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            Hidden
          </label>
        </div>

        {end < start && (
          <p className="text-[11px] text-amber-700">End day is before start — it’ll be clamped to the start day.</p>
        )}

        <div className="flex items-center justify-between pt-1">
          <button
            onClick={onDelete}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
          >
            Delete
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={apply}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
